import React, { useState } from 'react';
import axios from 'axios';

export default function Login({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [rememberMe, setRememberMe] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [loginPending, setLoginPending] = useState(false);

  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerMessage, setRegisterMessage] = useState('');
  const [registerPending, setRegisterPending] = useState(false);

  const loginTone = loginMessage.toLowerCase().includes('successful') ? 'success' : 'error';
  const registerTone = registerMessage.toLowerCase().includes('successful') ? 'success' : 'error';
  const authHeading = mode === 'login' ? 'Sign in to your account.' : 'Create your account.';
  const authCopy = mode === 'login'
    ? 'Access your orders, saved details, and account tools.'
    : 'Set up your profile to continue with Rive Moderne.';

  const activateMode = (nextMode) => {
    setMode(nextMode);
    if (nextMode === 'login') {
      setRegisterMessage('');
      return;
    }
    setLoginMessage('');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginPending(true);
    setLoginMessage('');

    try {
      const response = await axios.post('/api/auth/login', {
        email: loginEmail.trim(),
        password: loginPassword,
      });

      const storage = rememberMe ? window.localStorage : window.sessionStorage;
      const otherStorage = rememberMe ? window.sessionStorage : window.localStorage;
      otherStorage.removeItem('rivemoderne_token');
      otherStorage.removeItem('rivemoderne_user');

      storage.setItem('rivemoderne_token', response.data.token);
      storage.setItem('rivemoderne_user', JSON.stringify(response.data.user));

      if (onLogin) onLogin(response.data.user);

      setLoginMessage('Login successful');
      window.location.href = response.data.user.user_type === 'admin' ? '/admin' : '/';
    } catch (error) {
      setLoginMessage(error.response?.data?.error || 'Login failed');
    } finally {
      setLoginPending(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegisterPending(true);
    setRegisterMessage('');

    if (registerPassword !== registerConfirmPassword) {
      setRegisterMessage('Passwords do not match');
      setRegisterPending(false);
      return;
    }

    try {
      await axios.post('/api/auth/register', {
        name: registerName.trim(),
        user_type: 'user',
        email: registerEmail.trim(),
        password: registerPassword,
      });

      setLoginEmail(registerEmail.trim());
      setLoginPassword(registerPassword);
      setRegisterName('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
      setRegisterMessage('');
      setLoginMessage('Account created. Sign in now.');
      setMode('login');
    } catch (error) {
      setRegisterMessage(error.response?.data?.error || 'Registration failed');
    } finally {
      setRegisterPending(false);
    }
  };

  return (
    <section className="auth-page">
      <div className={`auth-panel auth-panel--${mode} section-card`}>
        <div className="auth-header">
          <span className="auth-brand">Rive Moderne</span>
          <div className="auth-copy-block">
            <h1 className="auth-title">{authHeading}</h1>
            <p className="auth-copy">{authCopy}</p>
          </div>
        </div>

        <div className="auth-tabs" role="tablist" aria-label="Authentication">
          <button
            className={`auth-tab${mode === 'login' ? ' is-active' : ''}`}
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            onClick={() => activateMode('login')}
          >
            Sign in
          </button>
          <button
            className={`auth-tab${mode === 'register' ? ' is-active' : ''}`}
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            onClick={() => activateMode('register')}
          >
            Sign up
          </button>
        </div>

        <div className="auth-viewport">
          <div className={`auth-track${mode === 'register' ? ' is-register' : ''}`}>
            <section className="auth-view" aria-hidden={mode !== 'login'}>
              <form className="auth-form" onSubmit={handleLogin}>
                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={loginEmail}
                    required
                    disabled={mode !== 'login'}
                    onChange={(event) => setLoginEmail(event.target.value)}
                    placeholder="name@example.com"
                  />
                </label>

                <label className="auth-field has-action">
                  <span>Password</span>
                  <span className="auth-input-wrap">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={loginPassword}
                      required
                      disabled={mode !== 'login'}
                      onChange={(event) => setLoginPassword(event.target.value)}
                      placeholder="Enter your password"
                    />
                    <button
                      className="auth-input-action"
                      type="button"
                      disabled={mode !== 'login'}
                      onClick={() => setShowLoginPassword((value) => !value)}
                    >
                      {showLoginPassword ? 'Hide' : 'Show'}
                    </button>
                  </span>
                </label>

                <label className="auth-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    disabled={mode !== 'login'}
                    onChange={(event) => setRememberMe(event.target.checked)}
                  />
                  <span className="auth-remember__switch" aria-hidden="true">
                    <span className="auth-remember__dot" />
                    <span className="auth-remember__state">{rememberMe ? 'YES' : 'NO'}</span>
                  </span>
                  <span className="auth-remember__text">Keep me signed in</span>
                </label>

                <button className="auth-submit" type="submit" disabled={mode !== 'login' || loginPending}>
                  {loginPending ? 'Signing in...' : 'Sign in'}
                </button>

                {loginMessage ? (
                  <p className={`auth-feedback auth-feedback--${loginTone}`}>{loginMessage}</p>
                ) : null}
              </form>
            </section>

            <section className="auth-view" aria-hidden={mode !== 'register'}>
              <form className="auth-form" onSubmit={handleRegister}>
                <label className="auth-field">
                  <span>Full name</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={registerName}
                    required
                    disabled={mode !== 'register'}
                    onChange={(event) => setRegisterName(event.target.value)}
                    placeholder="Your full name"
                  />
                </label>

                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={registerEmail}
                    required
                    disabled={mode !== 'register'}
                    onChange={(event) => setRegisterEmail(event.target.value)}
                    placeholder="name@example.com"
                  />
                </label>

                <label className="auth-field has-action">
                  <span>Password</span>
                  <span className="auth-input-wrap">
                    <input
                      type={showRegisterPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={registerPassword}
                      required
                      disabled={mode !== 'register'}
                      onChange={(event) => setRegisterPassword(event.target.value)}
                      placeholder="Create a password"
                    />
                    <button
                      className="auth-input-action"
                      type="button"
                      disabled={mode !== 'register'}
                      onClick={() => setShowRegisterPassword((value) => !value)}
                    >
                      {showRegisterPassword ? 'Hide' : 'Show'}
                    </button>
                  </span>
                </label>

                <label className="auth-field has-action">
                  <span>Confirm password</span>
                  <span className="auth-input-wrap">
                    <input
                      type={showRegisterConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={registerConfirmPassword}
                      required
                      disabled={mode !== 'register'}
                      onChange={(event) => setRegisterConfirmPassword(event.target.value)}
                      placeholder="Repeat your password"
                    />
                    <button
                      className="auth-input-action"
                      type="button"
                      disabled={mode !== 'register'}
                      onClick={() => setShowRegisterConfirmPassword((value) => !value)}
                    >
                      {showRegisterConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                  </span>
                </label>

                <button className="auth-submit" type="submit" disabled={mode !== 'register' || registerPending}>
                  {registerPending ? 'Creating account...' : 'Sign up'}
                </button>

                {registerMessage ? (
                  <p className={`auth-feedback auth-feedback--${registerTone}`}>{registerMessage}</p>
                ) : null}
              </form>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
}
